import React, { useState, useEffect } from 'react';
import { TableCell, TableRow, Table, TableBody } from '@material-ui/core';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import StatusButton from '@/components/UIKit/Buttons/StatusButton';
import { formatTime } from '@/utils/Helpers';
import Arrow from '@/assets/img/down_arrow.png';
import { Box } from 'UIKit/Grid';

const StyledTable = styled(Table)`
  transition: ease 0.3s all;
  z-index: 1;
  transform: translateY(-20%);
  opacity: 0;
  position: absolute;

  &.absolute {
    position: relative;
  }

  &.active {
    transform: translateY(0);
    opacity: 1;
  }
`;

const StyledButton = styled.div`
  width: 100%;
  height: 100%;
  padding: 12px;
  position: relative;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  &:hover {
    cursor: pointer;
  }
`;

const Img = styled.img`
  transition: ease 0.3s all;

  &.active {
    transform: rotate(-180deg);
  }
`;

const Wrap = styled.div`
  position: relative;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

function HomeworkSimulators({
  data,
  page,
  intl,
  rooms,
  component,
  getParamsText,
}) {
  const [visible, setVisible] = useState(false);
  const [anim, setAnim] = useState(false);

  useEffect(() => setAnim(visible), [visible]);

  return (
    <Wrap>
      <StyledButton onClick={() => setVisible(!visible)}>
        {intl.formatMessage({ id: visible ? 'HIDE' : 'VIEW' })}
        <Img
          src={Arrow}
          alt=""
          className={`${!visible && 'absolute'} ${anim && 'active'}`}
        />
      </StyledButton>
      <StyledTable
        style={{
          height: 'calc(100% + 2px)',
          margin: -1,
          width: 'calc(100% + 2px)',
        }}
        className={`${visible && 'absolute'} ${anim && 'active'}`}
      >
        <TableBody>
          {data.map((hw, index) => {
            const {
              count,
              simulator,
              tasks,
              id,
              params,
              comment,
              sound,
              sample,
              showInRow,
              success,
              overfill,
              overfail,
            } = hw;

            const isSchulte = simulator?.name === 'roomD';

            return (
              <TableRow key={index}>
                <TableCell width={page === 'new' ? '40%' : '25%'}>
                  {intl.formatMessage({ id: simulator?.name || 'error' })}
                </TableCell>
                <TableCell>
                  {getParamsText(
                    [{ ...params, count, comment, sound, showInRow, sample }],
                    rooms,
                    intl
                  )[0]?.settings.map((item, key) => (
                    <div key={id + key}>{item}</div>
                  ))}
                </TableCell>
                {['history', 'completed'].includes(page) && (
                  <TableCell width="140px">
                    <StatusButton
                      status="show"
                      onClick={() =>
                        component.handleOpenModal({
                          type: 'show',
                          data: hw,
                        })
                      }
                    />

                    {isSchulte ? (
                      <Box pt={2}>
                        <FormattedMessage
                          id="COMPLETION_TIME"
                          defaultMessage=""
                        />
                        :{' '}
                        <span style={{ color: success > 0 ? 'green' : 'red' }}>
                          {tasks?.[tasks.length - 1]?.timeSpent !== undefined
                            ? formatTime(tasks[tasks.length - 1].timeSpent)
                            : '00:00'}
                        </span>
                      </Box>
                    ) : (
                      <>
                        <Box pt={2}>
                          <FormattedMessage id="SUCCESS" defaultMessage="" />:{' '}
                          <span style={{ color: 'green' }}>{success}</span>/
                          {count}
                        </Box>

                        {(overfill > 0 || overfail > 0) && (
                          <Box pt={2}>
                            <FormattedMessage
                              id="OVERCOMPLETE"
                              defaultMessage=""
                            />
                            : <span style={{ color: 'green' }}>{overfill}</span>
                            /{overfill + overfail}
                          </Box>
                        )}
                      </>
                    )}
                  </TableCell>
                )}
                {page === 'await' && (
                  <>
                    <TableCell>
                      {count - tasks.length > 0
                        ? `${intl.formatMessage({
                            id: 'left_tasks',
                          })} ${count - tasks.length}`
                        : intl.formatMessage({
                            id: count - tasks.length ? 'overfill' : 'success',
                          })}
                    </TableCell>
                    <TableCell width="140px">
                      <StatusButton
                        status={
                          {
                            [tasks.length > 0]: 'process',
                            [tasks.length > count]: 'overfill',
                            [!tasks.length]: 'new',
                            [tasks.length === count]: 'success',
                          }.true
                        }
                        onClick={() => component.handleRun(id)}
                      />
                    </TableCell>
                  </>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </StyledTable>
    </Wrap>
  );
}

HomeworkSimulators.propTypes = {
  data: PropTypes.array,
  rooms: PropTypes.array.isRequired,
  intl: PropTypes.object.isRequired,
  page: PropTypes.string.isRequired,
  component: PropTypes.object.isRequired,
  getParamsText: PropTypes.func.isRequired,
};

HomeworkSimulators.defaultProps = {
  data: [],
};

export default HomeworkSimulators;
